/**
 * The 15 colors in a Colorful hand. The physical game ships 6 sets of
 * 15 cards (one set per player; the differing back designs let the
 * cards be told apart at the table). Digitally, we only need one set
 * — every player draws from the same 15 ids.
 *
 * Card images live at `public/games/colorful/card-NNN.webp`. For the
 * digital UI we use deck #1 (cards 001–030: odd = front, even = back).
 * `cardFront` is the front art for that color in deck 1; `cardBack`
 * is the deck 1 back (same image for every color in the deck).
 *
 * Hex values are eyeballed from the card images. Used as fallback
 * swatches when an `<img>` hasn't loaded yet, plus to colour matching
 * UI like the column dots on the map.
 */
export interface ColorCard {
  id: ColorId
  name: string
  hex: string
  /** Path to the front PNG. */
  cardFront: string
  /** Path to the deck-1 back PNG (uniform across the deck). */
  cardBack: string
}

export type ColorId =
  | 'white'
  | 'pale_blue'
  | 'black'
  | 'red'
  | 'blue'
  | 'lime'
  | 'gold'
  | 'green'
  | 'grey'
  | 'silver'
  | 'orange'
  | 'pink'
  | 'purple'
  | 'brown'
  | 'yellow'

const CARD_PATH = '/games/colorful/'
const DECK_ONE_BACK = `${CARD_PATH}card-002.webp`

// Card-number layout for deck 1: each colour's front sits at an odd
// index (1, 3, 5, …, 29). The order below matches the physical deck
// ordering as scanned.
function frontPath(n: number): string {
  return `${CARD_PATH}card-${String(n).padStart(3, '0')}.webp`
}

export const COLORS: ColorCard[] = [
  { id: 'white',     name: 'White',     hex: '#F8F8F5', cardFront: frontPath(1),  cardBack: DECK_ONE_BACK },
  { id: 'pale_blue', name: 'Pale Blue', hex: '#A0D8EF', cardFront: frontPath(3),  cardBack: DECK_ONE_BACK },
  { id: 'black',     name: 'Black',     hex: '#212121', cardFront: frontPath(5),  cardBack: DECK_ONE_BACK },
  { id: 'red',       name: 'Red',       hex: '#E63146', cardFront: frontPath(7),  cardBack: DECK_ONE_BACK },
  { id: 'blue',      name: 'Blue',      hex: '#2754A8', cardFront: frontPath(9),  cardBack: DECK_ONE_BACK },
  { id: 'lime',      name: 'Lime',      hex: '#B8DC4D', cardFront: frontPath(11), cardBack: DECK_ONE_BACK },
  { id: 'gold',      name: 'Gold',      hex: '#B58615', cardFront: frontPath(13), cardBack: DECK_ONE_BACK },
  { id: 'green',     name: 'Green',     hex: '#33994F', cardFront: frontPath(15), cardBack: DECK_ONE_BACK },
  { id: 'grey',      name: 'Grey',      hex: '#9CA0A0', cardFront: frontPath(17), cardBack: DECK_ONE_BACK },
  { id: 'silver',    name: 'Silver',    hex: '#C8CDD0', cardFront: frontPath(19), cardBack: DECK_ONE_BACK },
  { id: 'orange',    name: 'Orange',    hex: '#F58A2A', cardFront: frontPath(21), cardBack: DECK_ONE_BACK },
  { id: 'pink',      name: 'Pink',      hex: '#F58FBF', cardFront: frontPath(23), cardBack: DECK_ONE_BACK },
  { id: 'purple',    name: 'Purple',    hex: '#8C3BAB', cardFront: frontPath(25), cardBack: DECK_ONE_BACK },
  { id: 'brown',     name: 'Brown',     hex: '#A45F1F', cardFront: frontPath(27), cardBack: DECK_ONE_BACK },
  { id: 'yellow',    name: 'Yellow',    hex: '#F5DC2A', cardFront: frontPath(29), cardBack: DECK_ONE_BACK },
]

export const COLORS_BY_ID: Record<ColorId, ColorCard> =
  Object.fromEntries(COLORS.map(c => [c.id, c])) as Record<ColorId, ColorCard>

/**
 * Color words to reject if they appear in a custom-subject prompt.
 * Rule: "The chosen word can not include a color in any part of it."
 * (e.g. "bluejay" is invalid because it contains "blue".) Includes
 * common synonyms not in the palette ("gray", "tan") so the rule
 * covers any colour word the player might write.
 */
export const COLOR_WORDS: string[] = [
  ...COLORS.map(c => c.name.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, '')),
  ...COLORS.flatMap(c => c.name.toLowerCase().split(/[\s_]+/)),
  // Common synonyms / variants the rule still covers.
  'gray', 'tan', 'beige', 'navy', 'teal', 'cyan',
]
